import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useUpdateCompanyMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { CommonToast } from "../../../common/Toast";
import useCompanyFollowUpdate from "../../Dashboard/mycompanies/CompleteCompanyProfile/useCompanyFollowUpdate";
import { DOCUMENT_UPLOAD_PATH } from "./documentSpecs";

/* The three official documents' uploads. */
export const useDocumentUploads = () => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [updateCompany] = useUpdateCompanyMutation();
  const followUpdate = useCompanyFollowUpdate();
  const { refetch } = useGetCompanyQuery({ companyId: companyID, token });

  /* Which slot is mid-upload, so only that one shows a spinner. */
  const [busyField, setBusyField] = useState("");
  const [errors, setErrors] = useState({});

  const upload = async (spec, file) => {
    if (!file) return;
    setBusyField(spec.field);
    setErrors((prev) => ({ ...prev, [spec.field]: "" }));

    try {
      const uploaded = await uploadFileToS3(
        uploadFilePath?.[DOCUMENT_UPLOAD_PATH],
        file,
        token,
        companyID,
        spec.category
      );

      if (uploaded?.success === false) {
        /* uploadFileToS3 reports its own reason via a toast and resolves with
           success:false rather than throwing. */
        return;
      }
      if (!uploaded?.url) {
        /* Transcribed guard: the live code only PATCHes `if (fileString?.url)`
           — but said nothing when there was none, so the slot simply stayed
           empty with no explanation. */
        setErrors((prev) => ({
          ...prev,
          [spec.field]: "That upload didn't complete. Please try again.",
        }));
        return;
      }

      const body = new FormData();
      body.append(spec.field, uploaded.url);

      const updated = await updateCompany({
        token,
        companyId: companyID,
        data: body,
      }).unwrap();

      if (updated) {
        /* Transcribed: the live code writes the response to localStorage and
           dispatches it, so the rest of the flow sees the new document. */
        dispatch(setCurrentEditingCompany(updated));
        try {
          localStorage.setItem("currentEditingCompany", JSON.stringify(updated));
        } catch {
          /* A full or blocked localStorage costs nothing here — redux already
             has the company. */
        }
        Promise.resolve(refetch()).catch(() => {});
        await followUpdate(companyID, "Documents Updated", token);
        CommonToast.success(`${spec.label.replace(/^Upload /, "")} uploaded.`);
      }
    } catch (error) {
      /* The live handler's catch is empty, so a refused PATCH left the slot
         looking empty with no reason given. */
      const message =
        error?.data?.detail ||
        error?.data?.message ||
        error?.message ||
        "Couldn't save that document. Please try again.";
      setErrors((prev) => ({ ...prev, [spec.field]: message }));
      CommonToast.error(message);
    } finally {
      setBusyField("");
    }
  };

  return { company: currentEditingCompany, upload, busyField, errors };
};

export default useDocumentUploads;
